import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import List from './List';
import hooks from 'hooks';
import { $case, pluralize } from 'filters';
import ModalMessageSendToPage from 'components/modals/ModalMessageSendToPage';
import models from 'models';
import { useModal } from 'hooks/useModal';
import { useServices } from 'hooks/useServices';
import { useToast } from 'hooks/useToast';

const Customers = () => {
  const modal = useModal();
  const services = useServices();
  const toast = useToast();
  const [totalSearchList, setTotalSearchList] = useState({});
  const [renderFlag, setRenderFlag] = useState(true);
  const [checkItems, setCheckItems] = useState([]);

  const callApi = useCallback(async () => {
    let totalResp = {};
    let levelsParam = { limit: 300 };
    const levelResp = await services.crm.customer.level.all(levelsParam);
    if (!levelResp) return;
    totalResp.levels = levelResp.data;

    let complaintParams = { limit: 300 };
    const complaintResp = await services.crm.customer.complaint.all(
      complaintParams
    );
    if (!complaintResp) return;
    totalResp.complaint = complaintResp.data;

    let regionParams = { limit: 300 };
    const regionResp = await services.crm.customer.region.all(regionParams);
    if (!regionResp) return;
    totalResp.region = regionResp.data;

    let favoriteParams = { limit: 10000, visible: true };
    const favoriteResp = await services.crm.treatment.categories.items_categories_v2(
      favoriteParams
    );
    if (!favoriteResp) return;
    totalResp.favorites = favoriteResp.data;

    const jobResp = await services.crm.customer.job.all(regionParams);
    if (!jobResp) return;
    totalResp.jobs = jobResp.data;

    let doctorParams = { duty: '의사', userStatus: 'active', limit: 300 };
    const doctorResp = await services.crm.user.duty(doctorParams);
    if (!doctorResp) return;
    totalResp.doctors = doctorResp.data;

    let counselParams = { duty: '상담사', userStatus: 'active', limit: 300 };
    const counselResp = await services.crm.user.duty(counselParams);
    if (!counselResp) return;
    totalResp.counselors = counselResp.data;

    setTotalSearchList(totalResp);
    setRenderFlag(!renderFlag);
  }, [
    services.crm.customer.level,
    services.crm.customer.complaint,
    services.crm.customer.region,
    services.crm.treatment.categories,
    services.crm.customer.job,
    services.crm.user,
  ]);

  useEffect(() => {
    callApi();
  }, [callApi]);

  const onDeleteCustomer = useCallback(
    async (id) => {
      try {
        await services.crm.customer.delete(id);
        modal
          .confirm({
            type: 'SUCCESS',
            msg: '삭제되었습니다.',
          })
          .then(() => {
            setRenderFlag(!renderFlag);
          });
      } catch (e) {
        modal.confirm({
          type: 'ERROR',
          msg: e.description,
        });
      }
    },
    [services.crm.customer, toast, renderFlag]
  );

  const onClickDeleteCustomer = (customer) => {
    modal
      .basic({
        body: '삭제하시겠습니까?',
        buttons: [
          {
            text: 'CANCEL',
            class: 'btn-default',
          },
          {
            text: 'CONFIRM',
            class: 'btn-primary',
          },
        ],
      })
      .then((idx) => {
        if (idx === 1) {
          onDeleteCustomer(customer.id);
        }
      });
  };

  const onClickSendSms = () => {
    if (checkItems.length) {
      modal.custom({
        component: ModalMessageSendToPage,
        options: {
          page: 'customer',
          data: checkItems,
        },
      });
    } else {
      toast.error('문자전송할 고객을 선택하세요.');
    }

    return;
  };

  const onAllCheckAction = (data) => {
    const allChecked = (data || []).every((v) => v.$$checked === true);
    if (allChecked) {
      //전체 선택인 경우
      const list = (data || []).filter((v) => {
        return (
          v.$$checked === true &&
          checkItems.find((f) => f.id === v.id) === undefined
        );
      });
      setCheckItems([...checkItems, ...list]);
    } else {
      //전체 해제인 경우
      // eslint-disable-next-line array-callback-return
      const list = (checkItems || []).filter((v) => {
        if (data.find((f) => f.id === v.id) === undefined) {
          return v;
        }
      });
      setCheckItems([...list]);
    }
  };

  const onAction = (obj) => {
    let customer = obj.row;
    let model = obj.key;
    if (model.type === 'multiCheck') {
      let data = [...checkItems];
      if (customer.$$checked) {
        if ((data || []).find((v) => v.id === customer.id) === undefined) {
          data.push(customer);
        }
        setCheckItems([...data]);
      } else {
        let list = data.filter((v) => {
          return v.id !== customer.id;
        });

        setCheckItems([...list]);
      }
      return;
    }

    if (['고객명', 'CUSTOMER'].indexOf(model.title) !== -1) {
      hooks.openCustomerChart({ customer });
      return;
    }

    if (['STATUS'].indexOf(model.title) !== -1) {
      onClickDeleteCustomer(customer);
    }
  };

  const settingModels = useCallback(() => {
    const dataCallList = [
      'levels',
      'complaint',
      'region',
      'favorites',
      'jobs',
      'doctors',
      'counselors',
    ];
    const model = models.crm.customer;
    let found;
    dataCallList.forEach((v) => {
      found = (model.keys || []).find(
        (o) => o.title === $case.toConst(pluralize(v))
      );
      if (!found) return;
      found.selectOptions = (totalSearchList[v] || []).map((o) => {
        let labelKey = v === 'complaint' ? 'content' : 'name';
        o.$$label = o[labelKey];
        o.$$value = o.id;
        return o;
      });
    });
  }, [models.crm.customer, totalSearchList]);

  useEffect(() => {
    settingModels();
  }, [settingModels]);

  return (
    <List
      renderFlag={renderFlag}
      api={services.crm.customer.all}
      model={models.crm.customer}
      modelName={'customer'}
      onAction={onAction}
      onAllCheckAction={onAllCheckAction}
      onClickSendSms={onClickSendSms}
      checkItems={checkItems}
    />
  );
};

export default observer(Customers);
